import React, { useCallback, useEffect, useState } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Button,
  Badge,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import Flatpickr from 'react-flatpickr';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import DeleteModal from '../../Components/Common/DeleteModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import {
  getTenderAnnouncements,
  getTenderAnnouncementById,
  getAvailableProcurements,
  createTenderAnnouncement,
  updateTenderAnnouncement,
  deleteTenderAnnouncement,
  setTenderAnnouncementPublish,
  uploadTenderAnnouncementDocument,
  updateTenderAnnouncementDocument,
  deleteTenderAnnouncementDocument,
  getTenderAnnouncementDocumentViewUrl,
} from '../../helpers/tenderAnnouncement_helper';

// Empat nilai status (D-33). Disimpan PascalCase tanpa spasi; label berspasi
// hanya untuk tampilan — jangan pernah mengirim label ke API.
const STATUS_OPTIONS = ['Draft', 'Announced', 'BiddingOpen', 'BiddingClosed'];

const STATUS_LABEL = {
  Draft: 'Draft',
  Announced: 'Announced',
  BiddingOpen: 'Bidding Open',
  BiddingClosed: 'Bidding Closed',
};

const CONTRACT_TYPE_OPTIONS = ['Term', 'Spot'];

// Jam selalu 24 jam. Input bawaan browser (`type="time"`) tidak bisa dipaksa:
// formatnya mengikuti locale mesin pemakai dan atribut `lang` diabaikan Chrome,
// sehingga pemakai ber-locale 12 jam melihat AM/PM yang tidak diperlukan —
// zona waktunya sudah dinyatakan label WIB di sebelah kolom.
const TIME_PICKER_OPTIONS = {
  enableTime: true,
  noCalendar: true,
  dateFormat: 'H:i',
  time_24hr: true,
  allowInput: true,
};

// Dikunci ke dua nilai (D-22) dan divalidasi ulang di backend. Nilai lama
// 'Hydro & Condensate' sudah dinormalkan lewat migration — jangan dihidupkan lagi.
const PROCUREMENT_CATEGORY_OPTIONS = ['Product Hydro', 'Crude & Condensate'];

// Asal data (D-31). Nilai dikirim apa adanya; labelnya urusan tampilan.
const DATA_SOURCE = {
  PROCUREMENT: 'Procurement',
  MANUAL: 'Manual',
};

const DATA_SOURCE_BADGE = {
  [DATA_SOURCE.PROCUREMENT]: {
    label: 'PLAN & PROCUREMENT',
    className: 'ta-badge--procurement',
  },
  [DATA_SOURCE.MANUAL]: {
    label: 'MANUAL ENTRY',
    className: 'ta-badge--manual',
  },
};

// Tiga mode penerbitan (D-39).
const PUBLISH_MODE = {
  UNPUBLISH: 'Unpublish',
  PUBLISH_NOW: 'PublishNow',
  SCHEDULE: 'Schedule',
};

const SUMMARY_CARDS = [
  {
    key: 'total',
    label: 'Total Tender Announcements',
    icon: 'ri-file-list-3-line',
    color: 'primary',
  },
  {
    key: 'published',
    label: 'Published',
    icon: 'ri-megaphone-line',
    color: 'success',
  },
  {
    key: 'scheduled',
    label: 'Scheduled',
    icon: 'ri-calendar-check-line',
    color: 'info',
  },
  {
    key: 'unpublished',
    label: 'Unpublished',
    icon: 'ri-draft-line',
    color: 'secondary',
  },
];

const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Sama dengan batas di backend (D-38, §5.9). Diperiksa juga di sini supaya
// admin tidak menunggu permintaan yang sudah pasti ditolak.
const MAX_ATTACHMENTS = 10;
const MAX_ACTION_LINKS = 10;

const EMPTY_FORM = {
  id: null,
  dataSource: DATA_SOURCE.MANUAL,
  procurementId: null,
  isReadOnlyFromProcurement: false,
  announcementNumber: '',
  title: '',
  titleEn: '',
  summary: '',
  summaryEn: '',
  procurementType: '',
  procurementTypeEn: '',
  procurementCategory: '',
  contractType: '',
  proposalSubmissionDeadline: '',
  proposalSubmissionTime: '',
  status: 'Draft',
  awardedToCompanyName: '',
  bids: 0,
  isPublished: false,
  publishStatus: 'Unpublished',
  scheduledPublishDate: '',
  scheduledPublishTime: '',
  actionLinks: [],
};

const JAKARTA_TIME_ZONE = 'Asia/Jakarta';

// Bagian tanggal/jam sebuah instant menurut waktu Jakarta — bukan menurut zona
// perangkat admin, yang bisa berbeda (D-25).
const jakartaParts = value => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: JAKARTA_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const lookup = type => parts.find(p => p.type === type)?.value ?? '';
  return {
    year: lookup('year'),
    month: lookup('month'),
    day: lookup('day'),
    hour: lookup('hour'),
    minute: lookup('minute'),
  };
};

const toDateInputValue = value => {
  const parts = jakartaParts(value);
  return parts ? `${parts.year}-${parts.month}-${parts.day}` : '';
};

const toTimeInputValue = value => {
  const parts = jakartaParts(value);
  return parts ? `${parts.hour}:${parts.minute}` : '';
};

// Menggabungkan isian tanggal dan jam menjadi instant berzona WIB. Offset
// ditulis eksplisit supaya nilainya tidak bergeser mengikuti zona perangkat.
const combineJakarta = (dateValue, timeValue) => {
  if (!dateValue) return null;
  const time = timeValue && timeValue.length >= 4 ? timeValue : '00:00';
  return `${dateValue}T${time}:00+07:00`;
};

const formatJakartaDateTime = value => {
  const parts = jakartaParts(value);
  if (!parts) return '—';
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const monthLabel = months[Number(parts.month) - 1] ?? parts.month;
  return `${parts.day} ${monthLabel} ${parts.year} ${parts.hour}:${parts.minute} WIB`;
};

const formatFileSize = bytes => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return '0 KB';
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

// Response shape from axios is inconsistent in this repo (depends on the
// order the global interceptor in `api_helper.js` is registered) — this
// helper drills down to the `ApiResponse` `data` field, same fallback
// pattern already used by `LawView.js`/`DocumentUploadMasterView.js`.
const extractApiData = response => {
  const result = response?.data ?? response;
  return result?.data ?? result ?? {};
};

// The global axios interceptor (`api_helper.js`) rejects errors with a
// plain STRING, not an Error object — so `error.message` is always
// `undefined` for network failures. This helper handles both that shape
// and a regular axios error object (for any other axios instance that
// does not go through the interceptor).
const extractErrorMessage = (error, fallback) => {
  if (typeof error === 'string') return error;
  return error?.response?.data?.message || error?.message || fallback;
};

const TenderAnnouncementView = () => {
  const [data, setData] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [loading, setLoading] = useState(false);

  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPublished, setFilterPublished] = useState('all');
  const [filterDataSource, setFilterDataSource] = useState('all');

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [modalForm, setModalForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Pemilihan mode saat Create New (D-34): dari procurement, atau manual.
  const [modeModal, setModeModal] = useState(false);
  const [procurementModal, setProcurementModal] = useState(false);
  const [procurementSearch, setProcurementSearch] = useState('');
  const [procurementList, setProcurementList] = useState([]);
  const [procurementLoading, setProcurementLoading] = useState(false);

  // Attachments live inside the same add/edit modal (matches
  // `DocumentUploadMasterView.js`: one form, one Save action). Existing
  // rows (`documentList`) are only present once the announcement has an
  // id; newly picked files are staged client-side and uploaded together
  // with the create/update call when Save is pressed.
  const [documentList, setDocumentList] = useState([]);
  const [stagedFiles, setStagedFiles] = useState([]);

  // Mode penerbitan yang dipilih admin pada form; dikirim setelah Save.
  const [publishMode, setPublishMode] = useState(null);

  document.title = 'Tender Announcement';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        PageNumber: currentPage,
        PageSize: rowsPerPage,
      };
      if (filterText) params.Search = filterText;
      if (filterStatus !== 'all') params.Status = filterStatus;
      if (filterPublished !== 'all')
        params.IsPublished = filterPublished === 'published';
      if (filterDataSource !== 'all') params.DataSource = filterDataSource;

      const response = await getTenderAnnouncements(params);
      const payload = extractApiData(response);
      const items = payload.items || [];
      setData({ items: Array.isArray(items) ? items : [] });
      setTotalRows(payload.totalItems || items.length || 0);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to load data.'));
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    filterText,
    filterStatus,
    filterPublished,
    filterDataSource,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterSearch = () => {
    setCurrentPage(1);
    fetchData();
  };

  const clearFilter = () => {
    setFilterText('');
    setFilterStatus('all');
    setFilterPublished('all');
    setFilterDataSource('all');
    setCurrentPage(1);
  };

  const handlePageChange = page => setCurrentPage(page);
  const handlePerRowsChange = newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  };

  // ------------------------------------------------------------------
  // Create New — pilih mode dulu (D-34)
  // ------------------------------------------------------------------

  const openModeModal = () => setModeModal(true);

  const startManualEntry = () => {
    setModeModal(false);
    setFormData({ ...EMPTY_FORM, dataSource: DATA_SOURCE.MANUAL });
    setDocumentList([]);
    setStagedFiles([]);
    setPublishMode(null);
    setModalForm(true);
  };

  const loadAvailableProcurements = useCallback(async search => {
    setProcurementLoading(true);
    try {
      const response = await getAvailableProcurements({
        search: search || undefined,
        pageNumber: 1,
        pageSize: 20,
      });
      const payload = extractApiData(response);
      setProcurementList(payload.items || []);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to load procurements.'));
      setProcurementList([]);
    } finally {
      setProcurementLoading(false);
    }
  }, []);

  const startFromProcurement = () => {
    setModeModal(false);
    setProcurementSearch('');
    setProcurementModal(true);
    loadAvailableProcurements('');
  };

  // Field turunan procurement diisi backend saat create — di sini hanya nilai
  // pratinjau supaya admin melihat apa yang akan tersimpan.
  const selectProcurement = procurement => {
    setProcurementModal(false);
    setFormData({
      ...EMPTY_FORM,
      dataSource: DATA_SOURCE.PROCUREMENT,
      procurementId: procurement.id,
      isReadOnlyFromProcurement: true,
      announcementNumber: procurement.procurementNumber || '',
      procurementCategory: procurement.procurementCategory || '',
      proposalSubmissionDeadline: toDateInputValue(
        procurement.proposalSubmissionDeadline
      ),
      proposalSubmissionTime: toTimeInputValue(
        procurement.proposalSubmissionDeadline
      ),
      title: procurement.titleProcurement || '',
      titleEn: procurement.titleProcurement || '',
    });
    setDocumentList([]);
    setStagedFiles([]);
    setPublishMode(null);
    setModalForm(true);
  };

  const openEditModal = async row => {
    try {
      const response = await getTenderAnnouncementById(row.id);
      const item = extractApiData(response);
      setFormData({
        id: item.id,
        dataSource: item.dataSource || DATA_SOURCE.MANUAL,
        procurementId: item.procurementId || null,
        isReadOnlyFromProcurement: Boolean(item.isReadOnlyFromProcurement),
        announcementNumber: item.announcementNumber || '',
        title: item.title || '',
        titleEn: item.titleEn || '',
        summary: item.summary || '',
        summaryEn: item.summaryEn || '',
        procurementType: item.procurementType || '',
        procurementTypeEn: item.procurementTypeEn || '',
        procurementCategory: item.procurementCategory || '',
        contractType: item.contractType || '',
        proposalSubmissionDeadline: toDateInputValue(
          item.proposalSubmissionDeadline
        ),
        proposalSubmissionTime: toTimeInputValue(
          item.proposalSubmissionDeadline
        ),
        status: item.status || 'Draft',
        awardedToCompanyName: item.awardedToCompanyName || '',
        bids: item.bids ?? 0,
        isPublished: Boolean(item.isPublished),
        publishStatus: item.publishStatus || 'Unpublished',
        scheduledPublishDate: toDateInputValue(item.scheduledPublishDate),
        scheduledPublishTime: toTimeInputValue(item.scheduledPublishDate),
        actionLinks: (item.actionLinks || []).map(link => ({
          description: link.description || '',
          buttonLabel: link.buttonLabel || '',
          targetUrl: link.targetUrl || '',
          displayOrder: link.displayOrder ?? 0,
        })),
      });
      setDocumentList(item.documents || []);
      setStagedFiles([]);
      setPublishMode(null);
      setModalForm(true);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to load detail.'));
    }
  };

  const handleFormChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isReadOnly = formData.isReadOnlyFromProcurement;

  // ------------------------------------------------------------------
  // Action Link (D-37)
  // ------------------------------------------------------------------

  const addActionLink = () => {
    if (formData.actionLinks.length >= MAX_ACTION_LINKS) {
      toast.error(`Maximum ${MAX_ACTION_LINKS} action links per announcement.`);
      return;
    }
    setFormData(prev => ({
      ...prev,
      actionLinks: [
        ...prev.actionLinks,
        {
          description: '',
          buttonLabel: '',
          targetUrl: '',
          displayOrder: prev.actionLinks.length,
        },
      ],
    }));
  };

  const updateActionLink = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      actionLinks: prev.actionLinks.map((link, i) =>
        i === index ? { ...link, [field]: value } : link
      ),
    }));
  };

  const removeActionLink = index => {
    setFormData(prev => ({
      ...prev,
      actionLinks: prev.actionLinks
        .filter((_, i) => i !== index)
        .map((link, i) => ({ ...link, displayOrder: i })),
    }));
  };

  const buildPayload = () => {
    const payload = {
      title: formData.title,
      titleEn: formData.titleEn || null,
      summary: formData.summary || null,
      summaryEn: formData.summaryEn || null,
      awardedToCompanyName: formData.awardedToCompanyName || null,
      actionLinks: formData.actionLinks.map((link, index) => ({
        description: link.description,
        buttonLabel: link.buttonLabel,
        targetUrl: link.targetUrl,
        displayOrder: index,
      })),
    };

    // Field turunan procurement sengaja tidak dikirim untuk baris Procurement —
    // backend mengabaikannya (§4), jadi mengirimnya hanya menyesatkan pembaca.
    if (!isReadOnly) {
      payload.announcementNumber = formData.announcementNumber;
      payload.procurementType = formData.procurementType || null;
      payload.procurementTypeEn = formData.procurementTypeEn || null;
      payload.procurementCategory = formData.procurementCategory || null;
      payload.contractType = formData.contractType || null;
      payload.proposalSubmissionDeadline = combineJakarta(
        formData.proposalSubmissionDeadline,
        formData.proposalSubmissionTime
      );
      payload.status = formData.status;
      payload.bids = Number(formData.bids) || 0;
    }

    if (!formData.id) {
      payload.dataSource = formData.dataSource;
      payload.procurementId =
        formData.dataSource === DATA_SOURCE.PROCUREMENT
          ? formData.procurementId
          : null;
    }

    return payload;
  };

  const validateForm = () => {
    if (!formData.title?.trim()) {
      toast.error('Title (Indonesian) is required.');
      return false;
    }
    if (!isReadOnly) {
      if (!formData.announcementNumber?.trim()) {
        toast.error('Announcement number is required.');
        return false;
      }
      if (!formData.status) {
        toast.error('Status is required.');
        return false;
      }
      if (Number(formData.bids) < 0) {
        toast.error('Bids cannot be negative.');
        return false;
      }
    }

    for (const [index, link] of formData.actionLinks.entries()) {
      const position = index + 1;
      if (!link.description?.trim()) {
        toast.error(`Action link ${position}: description is required.`);
        return false;
      }
      if (!link.buttonLabel?.trim()) {
        toast.error(`Action link ${position}: button label is required.`);
        return false;
      }
      if (!/^https?:\/\//i.test(link.targetUrl?.trim() || '')) {
        toast.error(
          `Action link ${position}: target URL must start with http:// or https://.`
        );
        return false;
      }
    }

    if (publishMode === PUBLISH_MODE.SCHEDULE) {
      if (!formData.scheduledPublishDate) {
        toast.error('Scheduled date is required.');
        return false;
      }
      const scheduled = new Date(
        combineJakarta(
          formData.scheduledPublishDate,
          formData.scheduledPublishTime
        )
      );
      if (scheduled.getTime() <= Date.now()) {
        toast.error('Scheduled date and time must be in the future.');
        return false;
      }
    }

    return true;
  };

  const handleAddStagedFiles = e => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    const accepted = [];
    for (const file of files) {
      if (file.type !== 'application/pdf') {
        toast.error(`"${file.name}" was skipped — only PDF files are allowed.`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" was skipped — maximum file size is 10MB.`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) return;

    // Batas dihitung dari lampiran tersimpan DITAMBAH yang masih menunggu —
    // kalau tidak, admin baru tahu batasnya setelah separuh berkas terkirim.
    const room = MAX_ATTACHMENTS - documentList.length - stagedFiles.length;
    if (room <= 0) {
      toast.error(`Maximum ${MAX_ATTACHMENTS} attachments per announcement.`);
      return;
    }
    if (accepted.length > room) {
      toast.error(
        `Only ${room} more attachment(s) can be added — maximum is ${MAX_ATTACHMENTS}.`
      );
    }

    setStagedFiles(prev => [...prev, ...accepted.slice(0, room)]);
  };

  const removeStagedFile = index => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Create/update the record, then upload any newly staged files — one
  // Save action covers both, same as `DocumentUploadMasterView.js`.
  const handleSaveForm = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      let announcementId = formData.id;

      if (announcementId) {
        await updateTenderAnnouncement(announcementId, payload);
      } else {
        const createResponse = await createTenderAnnouncement(payload);
        announcementId = extractApiData(createResponse).id;
      }

      // Berkas yang gagal tetap ditahan supaya admin bisa mencoba lagi tanpa
      // memilih ulang; namanya disebut supaya jelas mana yang bermasalah.
      const failedFiles = [];
      for (const file of stagedFiles) {
        try {
          await uploadTenderAnnouncementDocument(announcementId, file);
        } catch (error) {
          failedFiles.push(file);
        }
      }

      if (publishMode) {
        try {
          await setTenderAnnouncementPublish(
            announcementId,
            publishMode,
            publishMode === PUBLISH_MODE.SCHEDULE
              ? combineJakarta(
                  formData.scheduledPublishDate,
                  formData.scheduledPublishTime
                )
              : null
          );
        } catch (error) {
          toast.error(
            extractErrorMessage(error, 'Failed to change publish status.')
          );
        }
      }

      if (failedFiles.length > 0) {
        setStagedFiles(failedFiles);
        toast.error(
          `Saved, but these attachments failed to upload: ${failedFiles
            .map(f => f.name)
            .join(', ')}`
        );
      } else {
        toast.success(
          formData.id
            ? 'Tender Announcement updated successfully.'
            : 'Tender Announcement added successfully.'
        );
        setModalForm(false);
        setStagedFiles([]);
      }

      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to save data.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = row => {
    setSelectedRow(row);
    setDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRow) return;
    try {
      await deleteTenderAnnouncement(selectedRow.id);
      toast.success('Tender Announcement deleted successfully.');
      setDeleteModal(false);
      setSelectedRow(null);
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to delete.'));
    }
  };

  // Existing attachments (row already has an id) are managed immediately —
  // no reason to defer a toggle/delete on an already-saved file until Save
  // is pressed again.
  const reloadExistingAttachments = async () => {
    if (!formData.id) return;
    try {
      const response = await getTenderAnnouncementById(formData.id);
      const item = extractApiData(response);
      setDocumentList(item.documents || []);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to load attachments.'));
    }
  };

  const handleViewDocument = async documentId => {
    try {
      const response = await getTenderAnnouncementDocumentViewUrl(documentId);
      const { url } = extractApiData(response);
      if (!url) {
        toast.error('Failed to open attachment.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to open attachment.'));
    }
  };

  const handleToggleShowToPublic = async doc => {
    try {
      await updateTenderAnnouncementDocument(doc.id, {
        isShowToPublic: !doc.isShowToPublic,
      });
      reloadExistingAttachments();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to update attachment.'));
    }
  };

  // Menukar displayOrder dua lampiran bertetangga. Urutan ini yang dipakai
  // halaman publik, jadi perubahannya langsung dikirim, bukan ditunda ke Save.
  const moveAttachment = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= documentList.length) return;

    const current = documentList[index];
    const neighbour = documentList[target];

    try {
      await updateTenderAnnouncementDocument(current.id, {
        displayOrder: neighbour.displayOrder,
      });
      await updateTenderAnnouncementDocument(neighbour.id, {
        displayOrder: current.displayOrder,
      });
      reloadExistingAttachments();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to reorder attachment.'));
    }
  };

  const handleDeleteDocument = doc => {
    Swal.fire({
      title: 'Delete this attachment?',
      text: doc.fileName,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    }).then(async result => {
      if (!result.isConfirmed) return;
      try {
        await deleteTenderAnnouncementDocument(doc.id);
        toast.success('Attachment deleted successfully.');
        reloadExistingAttachments();
      } catch (error) {
        toast.error(extractErrorMessage(error, 'Failed to delete attachment.'));
      }
    });
  };

  const statusBadgeColor = status => {
    switch (status) {
      case 'BiddingOpen':
        return 'success';
      case 'BiddingClosed':
        return 'danger';
      case 'Announced':
        return 'info';
      case 'Draft':
        return 'secondary';
      default:
        return 'light';
    }
  };

  const publishBadgeColor = publishStatus => {
    switch (publishStatus) {
      case 'Published':
        return 'success';
      case 'Scheduled':
        return 'info';
      default:
        return 'secondary';
    }
  };

  // Dihitung dari `totalRows` (seluruh baris hidup menurut server) dan dari
  // halaman yang sedang tampil untuk pecahan statusnya.
  const summaryCounts = {
    total: totalRows,
    published: (data.items || []).filter(i => i.publishStatus === 'Published')
      .length,
    scheduled: (data.items || []).filter(i => i.publishStatus === 'Scheduled')
      .length,
    unpublished: (data.items || []).filter(
      i => i.publishStatus === 'Unpublished'
    ).length,
  };

  const headerCell = label => (
    <span className='form-label text-uppercase fw-semibold fs-13'>{label}</span>
  );

  // Delapan kolom (D-42). Lebarnya tetap dan tabelnya menggulung horizontal —
  // memaksa semuanya muat membuat Tender Info tidak terbaca.
  const columns = [
    {
      name: headerCell('Created Date'),
      selector: row => row.createdDate,
      sortable: true,
      width: '11rem',
      cell: row => (
        <span className='text-muted fs-12'>
          {formatJakartaDateTime(row.createdDate)}
        </span>
      ),
    },
    {
      name: headerCell('Updated Date'),
      selector: row => row.updatedDate,
      sortable: true,
      width: '11rem',
      cell: row => (
        <span className='text-muted fs-12'>
          {row.updatedDate ? formatJakartaDateTime(row.updatedDate) : '—'}
        </span>
      ),
    },
    {
      name: headerCell('Tender Info'),
      selector: row => row.title,
      sortable: true,
      minWidth: '20rem',
      grow: 2,
      wrap: true,
      cell: row => (
        <div className='py-1'>
          <h6 className='mb-0'>{row.title}</h6>
          <div className='text-muted fs-12'>{row.announcementNumber}</div>
        </div>
      ),
    },
    {
      name: headerCell('Data Source'),
      selector: row => row.dataSource,
      sortable: true,
      width: '11.5rem',
      cell: row => {
        const badge = DATA_SOURCE_BADGE[row.dataSource];
        if (!badge) return <span className='text-muted'>—</span>;
        return (
          <span className={`ta-badge ${badge.className}`}>{badge.label}</span>
        );
      },
    },
    {
      name: headerCell('Type'),
      selector: row => row.procurementCategory,
      sortable: true,
      width: '10rem',
      wrap: true,
      cell: row =>
        row.procurementCategory ? (
          <Badge color='light' className='text-body'>
            {row.procurementCategory}
          </Badge>
        ) : (
          <span className='text-muted'>—</span>
        ),
    },
    {
      name: headerCell('Status Tender'),
      selector: row => row.status,
      sortable: true,
      width: '9.5rem',
      cell: row => (
        <Badge color={statusBadgeColor(row.status)}>
          {STATUS_LABEL[row.status] || row.status}
        </Badge>
      ),
    },
    {
      name: headerCell('Status Publish'),
      selector: row => row.publishStatus,
      sortable: true,
      width: '9.5rem',
      cell: row => (
        <Badge color={publishBadgeColor(row.publishStatus)}>
          {row.publishStatus}
        </Badge>
      ),
    },
    {
      name: headerCell('Actions'),
      width: '7rem',
      center: true,
      sortable: false,
      // Terbitkan dan Lampiran sengaja TIDAK ada di sini — keduanya ada di
      // dalam form ubah (D-29). Baris cukup ubah dan hapus.
      cell: row => (
        <div className='d-flex gap-1 justify-content-end'>
          <button
            className='btn btn-soft-info btn-sm btn-icon'
            title='Edit'
            onClick={() => openEditModal(row)}
          >
            <i className='ri-pencil-fill align-bottom'></i>
          </button>
          <button
            className='btn btn-soft-danger btn-sm btn-icon'
            title='Delete'
            onClick={() => handleDeleteClick(row)}
          >
            <i className='ri-delete-bin-6-line align-bottom'></i>
          </button>
        </div>
      ),
    },
  ];

  // Publikasi tidak boleh dilakukan selama status masih Draft (D-45): barisnya
  // tidak akan muncul di halaman publik walaupun IsPublished tersimpan true.
  const isDraftStatus = formData.status === 'Draft';
  const isClosedStatus = formData.status === 'BiddingClosed';
  const hasPendingSchedule = formData.publishStatus === 'Scheduled';

  const middleCardLabel = formData.isPublished
    ? 'Currently Published'
    : hasPendingSchedule
      ? 'Force Publish Now'
      : 'Publish Immediately';

  const middleCardDesc = formData.isPublished
    ? 'This announcement is currently live and visible to the public.'
    : hasPendingSchedule
      ? 'Override schedule and go live instantly.'
      : 'Go live instantly. The announcement date will record the current time.';

  const selectedPublishCard =
    publishMode ??
    (formData.publishStatus === 'Published'
      ? PUBLISH_MODE.PUBLISH_NOW
      : formData.publishStatus === 'Scheduled'
        ? PUBLISH_MODE.SCHEDULE
        : PUBLISH_MODE.UNPUBLISH);

  const renderPublishCard = (mode, icon, title, desc, disabled) => (
    <Col md={4}>
      <button
        type='button'
        disabled={disabled}
        onClick={() => setPublishMode(mode)}
        className={`ta-publish-card ${
          selectedPublishCard === mode ? 'is-selected' : ''
        } ${disabled ? 'is-disabled' : ''}`}
      >
        <i className={`${icon} ta-publish-card__icon`} aria-hidden='true' />
        <span className='ta-publish-card__title'>{title}</span>
        <span className='ta-publish-card__desc'>{desc}</span>
        {selectedPublishCard === mode && !disabled && (
          <i className='ri-checkbox-circle-fill ta-publish-card__check' />
        )}
      </button>
    </Col>
  );

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteConfirm}
          onCloseClick={() => setDeleteModal(false)}
        />
        <Container fluid>
          <BreadCrumb title='View Data' pageTitle='Tender Announcement' />

          <Row>
            <Col lg={12}>
              <Card id='TenderAnnouncementList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center'>
                    <div className='flex-grow-1'>
                      <h5 className='card-title mb-1'>
                        Announcements Management
                      </h5>
                      <p className='text-muted mb-0 fs-13'>
                        Manage and publish tender announcements.
                      </p>
                    </div>
                    <div className='flex-shrink-0'>
                      <button
                        type='button'
                        className='btn btn-primary btn-label right ms-auto nexttab btn-sm'
                        onClick={openModeModal}
                      >
                        <i className='ri-play-list-add-line label-icon align-middle fs-16 ms-2'></i>{' '}
                        <span className='d-none d-sm-inline'>Create New</span>
                        <span className='d-sm-none'>New</span>
                      </button>
                    </div>
                  </div>

                  {/* Empat kartu ringkasan. Angka dihitung dari data halaman yang
                      sedang tampil — sengaja TIDAK membuat endpoint statistik baru
                      (di luar cakupan). */}
                  <Row className='mt-3 g-2'>
                    {SUMMARY_CARDS.map(card => (
                      <Col md={3} sm={6} key={card.key}>
                        <div className='border rounded p-3 h-100'>
                          <div
                            className={`avatar-xs rounded bg-${card.color}-subtle text-${card.color} d-flex align-items-center justify-content-center mb-2`}
                          >
                            <i className={`${card.icon} fs-16`} />
                          </div>
                          <h4 className='mb-0'>{summaryCounts[card.key]}</h4>
                          <span className='text-muted fs-13'>{card.label}</span>
                        </div>
                      </Col>
                    ))}
                  </Row>

                  <div className='mt-3'>
                    <Row className='g-2 align-items-center w-100'>
                      <Col xxl={4} lg={12} md={12}>
                        <Input
                          type='text'
                          placeholder='Search announcements...'
                          value={filterText}
                          onChange={e => setFilterText(e.target.value)}
                          onKeyDown={e =>
                            e.key === 'Enter' && handleFilterSearch()
                          }
                        />
                      </Col>
                      <Col xxl={2} lg={4} md={6}>
                        <Input
                          type='select'
                          value={filterStatus}
                          onChange={e => setFilterStatus(e.target.value)}
                        >
                          <option value='all'>All Statuses</option>
                          {STATUS_OPTIONS.map(s => (
                            <option key={s} value={s}>
                              {STATUS_LABEL[s]}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col xxl={2} lg={4} md={6}>
                        <Input
                          type='select'
                          value={filterDataSource}
                          onChange={e => setFilterDataSource(e.target.value)}
                        >
                          <option value='all'>All Data Sources</option>
                          <option value={DATA_SOURCE.PROCUREMENT}>
                            Plan &amp; Procurement
                          </option>
                          <option value={DATA_SOURCE.MANUAL}>
                            Manual Entry
                          </option>
                        </Input>
                      </Col>
                      <Col xxl={2} lg={4} md={6}>
                        <Input
                          type='select'
                          value={filterPublished}
                          onChange={e => setFilterPublished(e.target.value)}
                        >
                          <option value='all'>All (Draft / Published)</option>
                          <option value='published'>Published</option>
                          <option value='draft'>Not Published</option>
                        </Input>
                      </Col>
                      <Col xxl={2} lg={12} md={6}>
                        <div className='d-flex gap-2'>
                          <Button
                            color='primary'
                            className='flex-grow-1'
                            onClick={handleFilterSearch}
                          >
                            <i className='ri-search-line align-bottom me-1'></i>
                            Search
                          </Button>
                          <Button color='light' onClick={clearFilter}>
                            <i className='ri-refresh-line align-bottom'></i>
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div className='table-responsive'>
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={data.items}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handlePerRowsChange}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      noDataComponent='No Tender Announcement data yet'
                    />
                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Pilihan mode saat Create New (D-34). */}
          <Modal
            isOpen={modeModal}
            centered
            toggle={() => setModeModal(!modeModal)}
          >
            <ModalHeader toggle={() => setModeModal(!modeModal)}>
              <span className='modal-title fw-bold'>
                New Tender Announcement
              </span>
            </ModalHeader>
            <ModalBody>
              <p className='text-muted'>
                Choose where the announcement data comes from. This cannot be
                changed later.
              </p>
              <Row className='g-3'>
                <Col md={6}>
                  <button
                    type='button'
                    className='ta-mode-card'
                    onClick={startFromProcurement}
                  >
                    <i className='ri-database-2-line ta-mode-card__icon' />
                    <span className='ta-mode-card__title'>
                      From Procurement
                    </span>
                    <span className='ta-mode-card__desc'>
                      Pick an existing procurement. Most fields stay in sync and
                      cannot be edited here.
                    </span>
                  </button>
                </Col>
                <Col md={6}>
                  <button
                    type='button'
                    className='ta-mode-card'
                    onClick={startManualEntry}
                  >
                    <i className='ri-edit-2-line ta-mode-card__icon' />
                    <span className='ta-mode-card__title'>Manual Entry</span>
                    <span className='ta-mode-card__desc'>
                      Type everything by hand. Not linked to any procurement.
                    </span>
                  </button>
                </Col>
              </Row>
            </ModalBody>
          </Modal>

          {/* Pencarian procurement yang belum punya pengumuman. */}
          <Modal
            isOpen={procurementModal}
            centered
            size='lg'
            toggle={() => setProcurementModal(!procurementModal)}
          >
            <ModalHeader toggle={() => setProcurementModal(!procurementModal)}>
              <span className='modal-title fw-bold'>Select Procurement</span>
            </ModalHeader>
            <ModalBody>
              <div className='d-flex gap-2 mb-3'>
                <Input
                  type='text'
                  placeholder='Search number / title...'
                  value={procurementSearch}
                  onChange={e => setProcurementSearch(e.target.value)}
                  onKeyDown={e =>
                    e.key === 'Enter' &&
                    loadAvailableProcurements(procurementSearch)
                  }
                />
                <Button
                  color='primary'
                  onClick={() => loadAvailableProcurements(procurementSearch)}
                >
                  <i className='ri-search-line align-bottom'></i>
                </Button>
              </div>

              {procurementLoading && (
                <p className='text-muted mb-0'>Loading…</p>
              )}

              {!procurementLoading && procurementList.length === 0 && (
                <p className='text-muted mb-0'>
                  No procurement available. Every submitted procurement already
                  has an announcement.
                </p>
              )}

              {!procurementLoading &&
                procurementList.map(item => (
                  <button
                    type='button'
                    key={item.id}
                    className='ta-procurement-row'
                    onClick={() => selectProcurement(item)}
                  >
                    <div className='flex-grow-1 text-start'>
                      <div className='fw-semibold'>{item.titleProcurement}</div>
                      <div className='text-muted fs-12'>
                        {item.procurementNumber}
                        {item.procurementCategory
                          ? ` · ${item.procurementCategory}`
                          : ''}
                      </div>
                    </div>
                    <span className='text-muted fs-12 text-nowrap'>
                      {item.proposalSubmissionDeadline
                        ? formatJakartaDateTime(item.proposalSubmissionDeadline)
                        : '—'}
                    </span>
                  </button>
                ))}
            </ModalBody>
          </Modal>

          {/* Add/edit modal — general data + bilingual text + attachments +
              action links + publication, all saved in one action. */}
          <Modal
            isOpen={modalForm}
            role='dialog'
            autoFocus
            centered
            id='tenderAnnouncementFormModal'
            toggle={() => setModalForm(!modalForm)}
            data-bs-backdrop='static'
            size='xl'
            className='tender-announcement-modal'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => setModalForm(!modalForm)}
            >
              <span className='modal-title text-info fw-bold me-2'>
                {formData.id
                  ? 'Edit Tender Announcement'
                  : 'Add Tender Announcement'}
              </span>
              {DATA_SOURCE_BADGE[formData.dataSource] && (
                <span
                  className={`ta-badge ${DATA_SOURCE_BADGE[formData.dataSource].className}`}
                >
                  {DATA_SOURCE_BADGE[formData.dataSource].label}
                </span>
              )}
            </ModalHeader>
            <ModalBody>
              <Card className='ta-section'>
                <CardBody>
                  <h6 className='ta-section__title'>
                    <i className='ri-list-check-2 me-2' />
                    Header
                  </h6>

                  {isReadOnly && (
                    <p className='text-muted small'>
                      Field bertanda abu-abu mengikuti data Procurement dan
                      tidak bisa diubah di sini.
                    </p>
                  )}

                  <Row className='g-3'>
                    <Col md={6}>
                      <Label className='form-label'>
                        Announcement Number {!isReadOnly && '*'}
                      </Label>
                      <Input
                        type='text'
                        name='announcementNumber'
                        value={formData.announcementNumber}
                        onChange={handleFormChange}
                        readOnly={isReadOnly}
                        disabled={isReadOnly}
                      />
                    </Col>
                    <Col md={6}>
                      <Label className='form-label'>
                        Status Tender {!isReadOnly && '*'}
                      </Label>
                      <Input
                        type='select'
                        name='status'
                        value={formData.status}
                        onChange={handleFormChange}
                        disabled={isReadOnly}
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s} value={s}>
                            {STATUS_LABEL[s]}
                          </option>
                        ))}
                      </Input>
                    </Col>

                    <Col md={12}>
                      <Label className='form-label'>
                        Proposal Submission Deadline (Jakarta Local Time)
                      </Label>
                      <div className='d-flex gap-2'>
                        <Input
                          type='date'
                          name='proposalSubmissionDeadline'
                          value={formData.proposalSubmissionDeadline}
                          onChange={handleFormChange}
                          readOnly={isReadOnly}
                          disabled={isReadOnly}
                        />
                        <div className='ta-time-field'>
                          <Flatpickr
                            className='form-control'
                            options={TIME_PICKER_OPTIONS}
                            value={formData.proposalSubmissionTime}
                            onChange={(dates, dateStr) =>
                              handleFormChange({
                                target: {
                                  name: 'proposalSubmissionTime',
                                  value: dateStr,
                                },
                              })
                            }
                            disabled={isReadOnly}
                          />
                          <span className='ta-time-field__suffix'>WIB</span>
                        </div>
                      </div>
                    </Col>

                    <Col md={6}>
                      <Label className='form-label'>Contract Type</Label>
                      <Input
                        type='select'
                        name='contractType'
                        value={formData.contractType}
                        onChange={handleFormChange}
                        disabled={isReadOnly}
                      >
                        <option value=''>—</option>
                        {CONTRACT_TYPE_OPTIONS.map(c => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={6}>
                      <Label className='form-label'>Procurement Category</Label>
                      <Input
                        type='select'
                        name='procurementCategory'
                        value={formData.procurementCategory}
                        onChange={handleFormChange}
                        disabled={isReadOnly}
                      >
                        <option value=''>—</option>
                        {PROCUREMENT_CATEGORY_OPTIONS.map(c => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </Input>
                    </Col>

                    <Col md={6}>
                      <Label className='form-label'>
                        Procurement Method — Indonesian
                      </Label>
                      <Input
                        type='text'
                        name='procurementType'
                        value={formData.procurementType}
                        onChange={handleFormChange}
                        readOnly={isReadOnly}
                        disabled={isReadOnly}
                      />
                    </Col>
                    <Col md={6}>
                      <Label className='form-label'>
                        Procurement Method — English
                      </Label>
                      {/* Sengaja tidak read-only walau baris berasal dari procurement:
                          procurement hanya menyimpan satu bahasa, jadi terjemahan
                          Inggrisnya milik admin dan tidak pernah ditimpa sinkronisasi. */}
                      <Input
                        type='text'
                        name='procurementTypeEn'
                        value={formData.procurementTypeEn}
                        onChange={handleFormChange}
                      />
                    </Col>

                    <Col md={6}>
                      <Label className='form-label'>Bids</Label>
                      <Input
                        type='number'
                        min='0'
                        name='bids'
                        value={formData.bids}
                        onChange={handleFormChange}
                        readOnly={isReadOnly}
                        disabled={isReadOnly}
                      />
                    </Col>
                    <Col md={6}>
                      <Label className='form-label'>Awarded To</Label>
                      <Input
                        type='text'
                        name='awardedToCompanyName'
                        value={formData.awardedToCompanyName}
                        onChange={handleFormChange}
                        readOnly={isReadOnly}
                        disabled={isReadOnly}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card className='ta-section'>
                <CardBody>
                  <h6 className='ta-section__title'>
                    <i className='ri-file-text-line me-2' />
                    Detail Tender Information
                  </h6>
                  <p className='ta-hint'>
                    If the English column is left empty, the Indonesian text
                    will be shown to the public.
                  </p>

                  <Row className='g-3'>
                    <Col md={6}>
                      <Label className='form-label'>Title — Indonesian *</Label>
                      <Input
                        type='text'
                        name='title'
                        value={formData.title}
                        onChange={handleFormChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label className='form-label'>Title — English</Label>
                      <Input
                        type='text'
                        name='titleEn'
                        value={formData.titleEn}
                        onChange={handleFormChange}
                      />
                    </Col>
                    <Col md={6}>
                      <Label className='form-label'>Summary — Indonesian</Label>
                      <textarea
                        className='form-control'
                        rows='5'
                        name='summary'
                        value={formData.summary}
                        onChange={handleFormChange}
                      ></textarea>
                    </Col>
                    <Col md={6}>
                      <Label className='form-label'>Summary — English</Label>
                      <textarea
                        className='form-control'
                        rows='5'
                        name='summaryEn'
                        value={formData.summaryEn}
                        onChange={handleFormChange}
                      ></textarea>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card className='ta-section'>
                <CardBody>
                  <div className='d-flex align-items-center mb-2'>
                    <h6 className='ta-section__title mb-0 flex-grow-1'>
                      <i className='ri-attachment-2 me-2' />
                      Attachments
                    </h6>
                  </div>

                  {!formData.id && (
                    <p className='text-muted small mb-2'>
                      Files picked here are uploaded once the announcement is
                      saved.
                    </p>
                  )}

                  {documentList.length === 0 && stagedFiles.length === 0 && (
                    <p className='text-muted small mb-2'>No attachments yet.</p>
                  )}

                  {documentList.map((doc, index) => (
                    <div
                      key={doc.id}
                      className='d-flex align-items-center gap-2 mb-2 p-2 border rounded'
                    >
                      <span className='ta-attachment-order'>{index + 1}</span>
                      <i className='ri-file-pdf-2-line fs-16 text-danger'></i>
                      <div className='flex-grow-1 min-w-0'>
                        <div className='small fw-semibold text-truncate'>
                          {doc.fileName}
                        </div>
                        <div
                          className='text-muted'
                          style={{ fontSize: '0.75rem' }}
                        >
                          {formatFileSize(doc.fileSizeBytes)}
                        </div>
                      </div>

                      <button
                        className='btn btn-soft-secondary btn-sm btn-icon'
                        title='Move up'
                        disabled={index === 0}
                        onClick={() => moveAttachment(index, -1)}
                      >
                        <i className='ri-arrow-up-line align-bottom'></i>
                      </button>
                      <button
                        className='btn btn-soft-secondary btn-sm btn-icon'
                        title='Move down'
                        disabled={index === documentList.length - 1}
                        onClick={() => moveAttachment(index, 1)}
                      >
                        <i className='ri-arrow-down-line align-bottom'></i>
                      </button>
                      <button
                        className='btn btn-soft-success btn-sm btn-icon'
                        title='View'
                        onClick={() => handleViewDocument(doc.id)}
                      >
                        <i className='ri-eye-fill align-bottom'></i>
                      </button>
                      <div
                        className='form-check form-switch me-2'
                        title='Show to public'
                      >
                        <Input
                          type='checkbox'
                          role='switch'
                          className='form-check-input'
                          checked={doc.isShowToPublic}
                          onChange={() => handleToggleShowToPublic(doc)}
                        />
                      </div>
                      <button
                        className='btn btn-soft-danger btn-sm btn-icon'
                        onClick={() => handleDeleteDocument(doc)}
                      >
                        <i className='ri-delete-bin-6-line align-bottom'></i>
                      </button>
                    </div>
                  ))}

                  {stagedFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className='d-flex align-items-center gap-2 mb-2 p-2 border border-dashed rounded'
                    >
                      <i className='ri-file-pdf-2-line fs-16 text-muted'></i>
                      <div className='flex-grow-1 min-w-0'>
                        <div className='small fw-semibold text-truncate'>
                          {file.name}
                        </div>
                        <div
                          className='text-muted'
                          style={{ fontSize: '0.75rem' }}
                        >
                          {formatFileSize(file.size)} — will be uploaded on Save
                        </div>
                      </div>
                      <button
                        className='btn btn-soft-danger btn-sm btn-icon'
                        onClick={() => removeStagedFile(index)}
                      >
                        <i className='ri-close-line align-bottom'></i>
                      </button>
                    </div>
                  ))}

                  <Input
                    type='file'
                    accept='application/pdf'
                    multiple
                    onChange={handleAddStagedFiles}
                    className='mt-1'
                  />
                  <small className='text-muted d-block mt-1'>
                    PDF only, max 10MB per file. You can attach multiple files
                    (maximum {MAX_ATTACHMENTS}).
                  </small>
                </CardBody>
              </Card>

              <Card className='ta-section'>
                <CardBody>
                  <div className='d-flex align-items-center mb-3'>
                    <h6 className='ta-section__title mb-0 flex-grow-1'>
                      <i className='ri-links-line me-2' />
                      Action Link
                    </h6>
                    <Button color='primary' size='sm' onClick={addActionLink}>
                      <i className='ri-add-line align-bottom me-1'></i>
                      Add Redirect Link
                    </Button>
                  </div>

                  {formData.actionLinks.length === 0 && (
                    <div className='ta-empty'>No redirect links added yet.</div>
                  )}

                  {formData.actionLinks.map((link, index) => (
                    <div key={index} className='ta-action-link'>
                      <Row className='g-2'>
                        <Col md={12}>
                          <Label className='form-label'>Description</Label>
                          <Input
                            type='text'
                            value={link.description}
                            onChange={e =>
                              updateActionLink(
                                index,
                                'description',
                                e.target.value
                              )
                            }
                          />
                        </Col>
                        <Col md={6}>
                          <Label className='form-label'>Button Label</Label>
                          <Input
                            type='text'
                            value={link.buttonLabel}
                            onChange={e =>
                              updateActionLink(
                                index,
                                'buttonLabel',
                                e.target.value
                              )
                            }
                          />
                        </Col>
                        <Col md={6}>
                          <Label className='form-label'>Target URL</Label>
                          <div className='d-flex gap-2'>
                            <Input
                              type='text'
                              placeholder='https://'
                              value={link.targetUrl}
                              onChange={e =>
                                updateActionLink(
                                  index,
                                  'targetUrl',
                                  e.target.value
                                )
                              }
                            />
                            <button
                              className='btn btn-soft-danger btn-icon'
                              title='Remove'
                              onClick={() => removeActionLink(index)}
                            >
                              <i className='ri-delete-bin-6-line align-bottom'></i>
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </CardBody>
              </Card>

              <Card className='ta-section'>
                <CardBody>
                  <h6 className='ta-section__title'>
                    <i className='ri-global-line me-2' />
                    Publication Setting
                  </h6>

                  {isDraftStatus && (
                    <p className='text-muted small'>
                      Publication is disabled while the tender status is Draft —
                      the announcement would not appear on the public page.
                    </p>
                  )}

                  <Row className='g-3'>
                    {renderPublishCard(
                      PUBLISH_MODE.UNPUBLISH,
                      'ri-global-line',
                      'Unpublished',
                      'Save as a private draft. Hidden from the public announcement page.',
                      false
                    )}
                    {renderPublishCard(
                      PUBLISH_MODE.PUBLISH_NOW,
                      'ri-global-line',
                      middleCardLabel,
                      middleCardDesc,
                      isDraftStatus
                    )}
                    {renderPublishCard(
                      PUBLISH_MODE.SCHEDULE,
                      'ri-calendar-schedule-line',
                      isClosedStatus || isDraftStatus
                        ? 'Schedule Disabled'
                        : 'Schedule Publication',
                      isClosedStatus
                        ? 'Cannot schedule publication for closed tenders.'
                        : isDraftStatus
                          ? 'Cannot schedule publication while status is Draft.'
                          : 'Set a specific future date and time for the announcement to automatically go live.',
                      isClosedStatus || isDraftStatus
                    )}
                  </Row>

                  {selectedPublishCard === PUBLISH_MODE.SCHEDULE &&
                    !isClosedStatus &&
                    !isDraftStatus && (
                      <div className='ta-schedule-box mt-3'>
                        <Label className='form-label fw-semibold'>
                          Set Scheduled Date &amp; Time
                        </Label>
                        <div className='d-flex gap-2'>
                          <Input
                            type='date'
                            name='scheduledPublishDate'
                            value={formData.scheduledPublishDate}
                            onChange={handleFormChange}
                          />
                          <div className='ta-time-field'>
                            <Flatpickr
                              className='form-control'
                              options={TIME_PICKER_OPTIONS}
                              value={formData.scheduledPublishTime}
                              onChange={(dates, dateStr) =>
                                handleFormChange({
                                  target: {
                                    name: 'scheduledPublishTime',
                                    value: dateStr,
                                  },
                                })
                              }
                            />
                            <span className='ta-time-field__suffix'>WIB</span>
                          </div>
                        </div>
                      </div>
                    )}
                </CardBody>
              </Card>
            </ModalBody>
            <ModalFooter>
              <Button
                color='light'
                className='btn-sm'
                onClick={() => setModalForm(false)}
              >
                Cancel
              </Button>
              <Button
                color='primary'
                className='btn-label btn-sm right'
                disabled={saving}
                onClick={handleSaveForm}
              >
                <i className='ri-database-2-line label-icon align-middle fs-16 ms-2'></i>{' '}
                {formData.id ? 'Save Changes' : 'Save'}
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TenderAnnouncementView;
